import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Box, Button, Popover, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { ArrowDropDown as ArrowDropDownIcon, ArrowDropUp as ArrowDropUpIcon } from '@mui/icons-material/';
import { UserPreference } from "../../interfaces/UserPreference";
import useUser from "../../hooks/useUser";
import { useDispatch } from "react-redux";
import { useErrorBoundary } from "react-error-boundary";
import PreferencesService from "../../services/preferences.service";
import { resetFilters, setFilters } from "../../../store/filters/filters.slice";
import { Filters } from "../../interfaces/Filters";
import classes from "./PreferencesDropdown.module.scss";
import { FiltersChipsNames } from "../../enums/FiltersChipsNames";
import StringHelper from "../../helpers/string.helper";
import PreferenceContext from "../../context/PreferenceContext";
import SubscribeButton from "../../views/subscribe-button/SubscribeButton";
import Notification from "../../views/notification/Notification";
import { NotificationSeverities } from "../../enums/NotificationSeverities";

const PreferencesDropdown = () => {
  const [preferences, setPreferences] = useState<UserPreference[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [notificationMessage, setNotificationMessage] = useState<string>('');
  const [isNotificationOpen, setIsNotificationOpen] = useState<boolean>(false);
  const { preferenceID, setPreferenceID } = useContext(PreferenceContext);
  const user = useUser();
  const dispatch = useDispatch();
  const { showBoundary } = useErrorBoundary();

  const fetchPreferences = useCallback(() => {
    if (user?.email) {
      PreferencesService.getByUserEmail(user.email)
        .then((preferences) => {
          setPreferences(preferences.filter((preference) => !preference.isForTipCollectionType));
          const existingPreference = preferences.find((preference) => preference.id === preferenceID);

          if (existingPreference) {
            setPreferenceID(existingPreference.id);
          }
        })
        .catch((error) => showBoundary(error));
    }
  }, [showBoundary, preferenceID, setPreferenceID, user]);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  const clearPreference = useCallback(() => {
    setPreferenceID(0);
    dispatch(resetFilters());
  }, [dispatch, setPreferenceID]);

  const toggleHandler = useCallback((id: number, checked: boolean) => {
    if (checked) {
      setPreferenceID(id);
      const preference = preferences.find((preference) => preference.id === id);

      if (preference) {
        const tags = preference.tags.map((tag) => ({
          id: tag.id,
          attributes: { name: tag.name, type: tag.type, tagger_id: tag.tagger_id, sport: tag.sport, gender: tag.gender },
        }));
        const filters: Filters = {
          hasWords: null,
          types: preference.types.map((type) => ({ id: type.id, name: type.name })),
          sites: preference.sites.map((site) => ({ id: site.id, name: site.name })),
          tags: tags,
          date: null,
          author: null,
          hasTips: preference.hasTips
        }
        dispatch(setFilters(filters));
      }
    } else {
      clearPreference();
    }
  }, [dispatch, preferences, clearPreference, setPreferenceID]);

  const buttonClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const getTipsText = (hasTips: boolean | null) => {
    if (hasTips === null) {
      return null;
    }

    return hasTips ? 'With Tips' : 'Without Tips';
  };

  const renderPreferences = useMemo(() => {
    const onSubscribeButtonSuccessHandler = (isSubscribed: boolean) => {
      setNotificationMessage(`Successfully ${!isSubscribed ? 'subscribed!' : 'unsubscribed!'}`);
      setIsNotificationOpen(true);
      fetchPreferences();
    };

    return <TableContainer>
      <Table>
        <TableHead className='my-5'>
          <TableRow>
            {[FiltersChipsNames.TYPE, FiltersChipsNames.SITE, FiltersChipsNames.TAG, FiltersChipsNames.TIP].map((name) => {
              return <TableCell key={StringHelper.generateRandomString(10)}>
                <Typography className={classes['header-text']}>{name}</Typography>
              </TableCell>
            })}
            <TableCell>
            </TableCell>
            <TableCell>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {preferences.map((preference) => (
            <TableRow key={preference.id}>
              <TableCell className={`${classes['cell']} py-1`} >
                <Typography className={`fw-bold ${classes['cell-text']}`}>
                  {preference.types.map((type) => (`${type.name} `)).join(' | ')}
                </Typography>
              </TableCell>
              <TableCell className={`${classes['cell']} py-1`} >
                <Typography className={`fw-bold ${classes['cell-text']}`}>
                  {preference.sites.map((site) => (`${site.name}`)).join(' | ')}
                </Typography>
              </TableCell>
              <TableCell className={`${classes['cell']} py-1`} >
                <Typography className={`fw-bold ${classes['cell-text']}`}>
                  {preference.tags.map((tag) => (`${tag.name}`)).join(' | ')}
                </Typography>
              </TableCell>
              <TableCell className={`${classes['cell']} py-1`} >
                <Typography className={`fw-bold ${classes['cell-text']}`}>
                  {getTipsText(preference.hasTips)}
                </Typography>
              </TableCell>
              <TableCell className={`${classes['cell']} py-1`} >
                <Switch
                  classes={{ checked: 'switch', track: 'switch-base' }}
                  edge='start'
                  checked={preferenceID === preference.id}
                  onChange={(event, checked) => toggleHandler(preference.id, checked)}
                />
              </TableCell>
              <TableCell className={`${classes['cell']} py-1`} >
                <SubscribeButton preference={preference} onSuccess={() => onSubscribeButtonSuccessHandler(preference.receiveNotifications)} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  }, [preferences, preferenceID, toggleHandler, fetchPreferences]);

  return <>
    {preferences.length > 0 &&
      <>
        <Box>
          <Button
            className='filter-button'
            onClick={buttonClickHandler}
            endIcon={anchorEl ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
          >
            My filters
            <Box className={`${classes['filters-number']} ms-2`}>
              <Typography textAlign='center' fontSize='12px' fontWeight='400px'>
                {preferences.length}
              </Typography>
            </Box>
          </Button>
        </Box>
        <Popover
          className='my-2'
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          {renderPreferences}
        </Popover>
      </>
    }
    <Notification
      open={isNotificationOpen}
      onClose={() => setIsNotificationOpen(false)}
      message={notificationMessage}
      severity={NotificationSeverities.SUCCESS}
      position={{ vertical: 'top', horizontal: 'right' }}
    />
  </>
};

export default PreferencesDropdown;
